import { forwardRef, useState, useRef, useEffect } from 'react';

import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { ko } from 'date-fns/locale';
import calendarIcon from '@shared/assets/images/calendar_icon.svg';

import styles from './DateInput.module.scss';

/**
 * DateInput 컴포넌트
 * - 텍스트 입력과 DatePicker를 모두 지원하는 날짜 입력 컴포넌트
 * - 인풋박스 클릭: 텍스트 입력 모드
 * - 캘린더 아이콘 클릭: DatePicker 모드
 *
 * @param {string} value - 날짜 값 (YYYY-MM-DD 형식)
 * @param {Function} onChange - 날짜 변경 핸들러
 * @param {string} placeholder - placeholder 텍스트
 * @param {Date} minDate - 최소 날짜
 * @param {Date} maxDate - 최대 날짜
 * @param {string} id - input id
 */
function DateInput({ value, onChange, placeholder, minDate, maxDate, id, ...props }) {
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');
  const inputRef = useRef(null);
  const datePickerRef = useRef(null);

  // value prop이 변경되면 inputValue 동기화 (편집 중이 아닐 때만, 그래야 년/월/일 중 일부만 수정 가능)
  useEffect(
    function () {
      if (document.activeElement === inputRef.current) return;
      if (!value) {
        setInputValue('');
        return;
      }
      const digits = value.replace(/[^0-9]/g, '');
      if (digits.length === 8) {
        setInputValue(`${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}`);
      } else {
        setInputValue(value);
      }
    },
    [value],
  );

  // 문자열 → Date 객체 변환
  function toDate(str) {
    if (!str) return null;
    const d = new Date(str);
    return isNaN(d.getTime()) ? null : d;
  }

  // Date 객체 → YYYY-MM-DD 문자열 변환
  function formatDate(date) {
    if (!date) return '';
    try {
      return date.toISOString().slice(0, 10);
    } catch {
      return '';
    }
  }

  // YYYY-MM-DD 구조 유지: 년(4자리)-월(2자리)-일(2자리), 빈 칸 허용 so 월만/일만 수정 가능
  function handleInputChange(e) {
    const raw = e.target.value.replace(/[^0-9-]/g, '');
    let p0 = '';
    let p1 = '';
    let p2 = '';
    if (raw.indexOf('-') === -1 && raw.length > 0) {
      p0 = raw.slice(0, 4);
      p1 = raw.slice(4, 6);
      p2 = raw.slice(6, 8);
    } else {
      const parts = raw.split('-');
      p0 = (parts[0] || '').replace(/[^0-9]/g, '').slice(0, 4);
      p1 = (parts[1] || '').replace(/[^0-9]/g, '').slice(0, 2);
      p2 = (parts[2] || '').replace(/[^0-9]/g, '').slice(0, 2);
    }
    setInputValue([p0, p1, p2].join('-'));
  }

  // 이전 값에서 년/월/일 부분 추출 (YYYY-MM-DD 또는 YYYYMMDD)
  function getPrevParts() {
    if (!value) return { year: '2024', month: '01', day: '01' };
    const digits = value.replace(/[^0-9]/g, '');
    if (digits.length >= 8) {
      return {
        year: digits.slice(0, 4),
        month: digits.slice(4, 6),
        day: digits.slice(6, 8),
      };
    }
    const p = value.split('-');
    return {
      year: (p[0] || '2024').slice(0, 4),
      month: (p[1] || '01').padStart(2, '0').slice(0, 2),
      day: (p[2] || '01').padStart(2, '0').slice(0, 2),
    };
  }

  // 입력 완료 시 (blur): 년/월/일 중 바꾼 부분만 반영하고 나머지는 이전 값 유지
  function handleInputBlur() {
    const trimmed = inputValue.trim();
    if (!trimmed) {
      onChange('');
      setInputValue('');
      return;
    }

    const prev = getPrevParts();
    const parts = trimmed.split('-');

    const yearStr = (parts[0] || '').replace(/[^0-9]/g, '');
    const monthStr = (parts[1] || '').replace(/[^0-9]/g, '');
    const dayStr = (parts[2] || '').replace(/[^0-9]/g, '');

    const year = yearStr.length >= 4 ? yearStr.slice(0, 4).padStart(4, '0') : prev.year;

    const monthNum =
      monthStr.length >= 1
        ? Math.min(12, Math.max(1, parseInt(monthStr.slice(0, 2), 10) || 1))
        : Math.min(12, Math.max(1, parseInt(prev.month, 10) || 1));
    const monthFinal = String(monthNum).padStart(2, '0');

    const dayNum =
      dayStr.length >= 1
        ? Math.min(31, Math.max(1, parseInt(dayStr.slice(0, 2), 10) || 1))
        : Math.min(31, Math.max(1, parseInt(prev.day, 10) || 1));
    const dayFinal = String(dayNum).padStart(2, '0');

    const formatted = `${year}-${monthFinal}-${dayFinal}`;
    const date = new Date(formatted);
    if (!isNaN(date.getTime())) {
      setInputValue(formatted);
      onChange(formatted);
    } else {
      setInputValue(value || '');
    }
  }

  // 캘린더 아이콘 클릭 시 DatePicker 열기
  function handleCalendarClick(e) {
    e.stopPropagation();
    setIsPickerOpen(true);
  }

  // DatePicker에서 날짜 선택 시
  function handleDatePickerChange(date) {
    const formatted = formatDate(date);
    setInputValue(formatted);
    onChange(formatted);
    setIsPickerOpen(false);
  }

  // 커스텀 DatePicker Input (숨김 처리)
  const CustomDatePickerInput = forwardRef(function CustomDatePickerInput(props, ref) {
    return <input ref={ref} {...props} style={{ display: 'none' }} />;
  });

  return (
    <div className={`${styles.dateInputContainer} ${props.className || ''}`} style={props.style}>
      <img
        src={calendarIcon}
        alt="캘린더"
        className={styles.calendarIcon}
        onClick={handleCalendarClick}
      />
      <input
        ref={inputRef}
        id={id}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        placeholder={placeholder}
        className={styles.dateInput}
      />
      {/* 숨겨진 DatePicker */}
      <DatePicker
        ref={datePickerRef}
        selected={toDate(value)}
        onChange={handleDatePickerChange}
        locale={ko}
        dateFormat="yyyy-MM-dd"
        customInput={<CustomDatePickerInput />}
        minDate={minDate}
        maxDate={maxDate}
        open={isPickerOpen}
        onClickOutside={() => setIsPickerOpen(false)}
        onCalendarClose={() => setIsPickerOpen(false)}
        popperProps={{
          strategy: 'fixed',
        }}
      />
    </div>
  );
}

export default DateInput;
